import { Copy, Link2 } from "lucide-react";
import { Notice } from "../../../components/ui/Notice";
import { Button } from "../../../components/ui/Button";
import { NotificationService } from "../../../services/notification";
import { useT } from "../../../i18n/admin";

// SAM-1171 — the payment link of a pass created with a pass_fee service. businessAccess answers it once, on create:
// the guest pays on /pay/{token} (Cardnet), and the pass SMS goes out only when it is paid.

export interface PassPaymentLink {
  pay_url?: string | null;
  amount?: number | string | null;
  currency?: string | null;
  expires_at?: string | null;
}

type Props = { link?: PassPaymentLink | null; sms?: { sent?: boolean } | null };

export const PassPaymentLinkPanel = ({ link, sms }: Props) => {
  const { t, fmt } = useT();
  if (!link?.pay_url) return null;
  const payUrl = link.pay_url;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(payUrl);
      NotificationService.success(t('invitation.linkCopied'));
    } catch {
      NotificationService.error(t('passLink.copyFailed'));
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid="pass-payment-link">
      <div style={{ width: '100%', height: 1, background: 'var(--line-subtle)', margin: '6px 0' }} />
      <h3 className="flex items-center gap-1.5" style={{ fontSize: 13, fontWeight: 500, margin: 0, color: 'var(--ink-900)' }}>
        <Link2 size={14} aria-hidden /> {t('passLink.title')}
      </h3>
      <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)' }}>
        {fmt.money(link.amount, link.currency)}
        {link.expires_at ? ` · ${t('passLink.expires', { time: fmt.time(link.expires_at) })}` : ''}
      </p>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={payUrl}
          aria-label={t('passLink.title')}
          className="flex-1 min-w-0"
          style={{ fontSize: 13, padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 'var(--r-8)', color: 'var(--ink-700)', background: 'var(--surface)' }}
          onFocus={(e) => e.currentTarget.select()}
        />
        <Button variant="ghost" onClick={copy} aria-label={t('passLink.copy')}>
          <Copy size={14} aria-hidden />
        </Button>
      </div>
      {sms?.sent === false && <Notice variant="warn">{t('passLink.smsFailed')}</Notice>}
    </div>
  );
};
